import api from './api';
import feedback from '@/components/ui/general/feedback/feedback';
import { CONFIG } from '@utils/constants';

/**
 * Join a game using a private code
 * @param {string} privateCode - The private code to join the game
 * @returns {Promise<object>} The response data from the API
 */
export const joinGameByCode = async (privateCode) => {
  try {
    if (!privateCode) {
      feedback.error("Private code is required");
      return null;
    }

    const response = await api.post(`/api/games/join/${privateCode}`);

    if (response.success) {
      feedback.success("Successfully joined the game!");
    }

    return response;

  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || "Failed to join game";

    feedback.error(`Unable to join game: ${errorMessage}`);

    if (CONFIG.DEBUG_MODE) {
      console.error("[dashboardApi.joinGameByCode] Error:", {
        privateCode,
        error: error.response?.data || error,
        status: error.response?.status
      });
    }

    return null;
  }
};

/**
 * Delete a game
 * @param {string} gameId - The ID of the game to delete
 * @returns {Promise<object>} The response data from the API
 */
export const deleteGame = async (gameId) => {
  try {
    if (!gameId) {
      feedback.error("Game ID is required");
      return null;
    }

    const response = await api.delete(`/api/games/${gameId}`);

    if (response.success) {
      feedback.success("Game deleted successfully");
    }

    return response;

  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || "Failed to delete game";

    feedback.error(`Unable to delete game: ${errorMessage}`);

    if (CONFIG.DEBUG_MODE) {
      console.error("[dashboardApi.deleteGame] Error:", {
        gameId,
        error: error.response?.data || error,
        status: error.response?.status
      });
    }

    return null;
  }
};

/**
 * Get list of games for dashboard
 * @returns {Promise<object>} The response data with games list
 */
export const getGamesList = async () => {
  try {
    const response = await api.get('/api/games');

    if (CONFIG.DEBUG_MODE) {
      console.log("[dashboardApi.getGamesList] Games loaded:", {
        count: response.data?.games?.length || 0,
        games: response.data?.games
      });
    }

    return response;

  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || "Failed to load games";

    feedback.error(`Unable to load games: ${errorMessage}`);

    if (CONFIG.DEBUG_MODE) {
      console.error("[dashboardApi.getGamesList] Error:", {
        error: error.response?.data || error,
        status: error.response?.status
      });
    }

    return null;
  }
};

/**
 * Get active player info
 * @returns {Promise<object>} The active player data
 */
export const getActivePlayer = async () => {
  try {
    const response = await api.get('/api/players/active');

    if (CONFIG.DEBUG_MODE) {
      console.log("[dashboardApi.getActivePlayer] Active player:", response.data);
    }

    return response;

  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || "Failed to get active player";

    feedback.error(`Unable to get active player: ${errorMessage}`);

    if (CONFIG.DEBUG_MODE) {
      console.error("[dashboardApi.getActivePlayer] Error:", {
        error: error.response?.data || error,
        status: error.response?.status
      });
    }

    return null;
  }
};

/**
 * Select a player as active
 * @param {string} playerId - The ID of the player to select
 * @returns {Promise<object>} The response data
 */
export const selectPlayer = async (playerId) => {
  try {
    if (!playerId) {
      feedback.error("Player ID is required");
      return null;
    }

    const response = await api.post(`/api/players/${playerId}/select`);

    if (response.success) {
      feedback.success("Player selected successfully");
    }

    return response;

  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || "Failed to select player";

    feedback.error(`Unable to select player: ${errorMessage}`);

    if (CONFIG.DEBUG_MODE) {
      console.error("[dashboardApi.selectPlayer] Error:", {
        playerId,
        error: error.response?.data || error,
        status: error.response?.status
      });
    }

    return null;
  }
};